/**
 * Payment hooks — subscriptions, pricing, cards, gifts, referral, history.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchActiveSubscription,
  fetchPricing,
  fetchPaymentHistory,
  fetchMyGifts,
  fetchUserCards,
  fetchReferralBalance,
  fetchReferralFriends,
  fetchPaymentStatus,
  createCard,
  verifyCard,
  removeCard,
  calculatePayment,
  validatePromo,
  createCheckout,
  subscribeWithCard,
  createGift,
  redeemGiftCode,
  requestWithdrawal,
} from '@turnifly/shared/api'
import type {
  SubscriptionData,
  PricingItem,
  PaymentHistoryItem,
  GiftSubscriptionItem,
  UserCard,
  ReferralBalance,
  ReferralFriend,
  PaymentCalculation,
  PromoValidation,
  CheckoutResult,
  SubscribeResult,
  CardCreateResult,
  CreateGiftResult,
  RedeemGiftResult,
  WithdrawalResult,
} from '@turnifly/shared/api'

export const paymentKeys = {
  subscription: (userId: string) => ['payment', 'subscription', userId] as const,
  pricing: ['payment', 'pricing'] as const,
  history: (userId: string) => ['payment', 'history', userId] as const,
  gifts: (userId: string) => ['payment', 'gifts', userId] as const,
  cards: ['payment', 'cards'] as const,
  referralBalance: (userId: string) => ['payment', 'referral-balance', userId] as const,
  referralFriends: (userId: string) => ['payment', 'referral-friends', userId] as const,
  paymentStatus: (paymentId: string) => ['payment', 'status', paymentId] as const,
} as const

// ── Queries ─────────────────────────────────────────────────

export function useActiveSubscription(
  userId: string | undefined,
): ReturnType<typeof useQuery<SubscriptionData | null>> {
  return useQuery({
    queryKey: paymentKeys.subscription(userId ?? ''),
    queryFn: () => fetchActiveSubscription(userId!),
    enabled: !!userId,
    staleTime: 60_000,
  })
}

export function usePricing(): ReturnType<typeof useQuery<PricingItem[]>> {
  return useQuery({
    queryKey: paymentKeys.pricing,
    queryFn: fetchPricing,
    staleTime: 5 * 60_000,
  })
}

export function usePaymentHistory(
  userId: string | undefined,
): ReturnType<typeof useQuery<PaymentHistoryItem[]>> {
  return useQuery({
    queryKey: paymentKeys.history(userId ?? ''),
    queryFn: () => fetchPaymentHistory(userId!),
    enabled: !!userId,
    staleTime: 60_000,
  })
}

export function useMyGifts(
  userId: string | undefined,
): ReturnType<typeof useQuery<GiftSubscriptionItem[]>> {
  return useQuery({
    queryKey: paymentKeys.gifts(userId ?? ''),
    queryFn: () => fetchMyGifts(userId!),
    enabled: !!userId,
    staleTime: 60_000,
  })
}

export function useUserCards(): ReturnType<typeof useQuery<UserCard[]>> {
  return useQuery({
    queryKey: paymentKeys.cards,
    queryFn: fetchUserCards,
    staleTime: 60_000,
  })
}

export function useReferralBalance(
  userId: string | undefined,
): ReturnType<typeof useQuery<ReferralBalance>> {
  return useQuery({
    queryKey: paymentKeys.referralBalance(userId ?? ''),
    queryFn: () => fetchReferralBalance(userId!),
    enabled: !!userId,
    staleTime: 60_000,
  })
}

export function useReferralFriends(
  userId: string | undefined,
): ReturnType<typeof useQuery<ReferralFriend[]>> {
  return useQuery({
    queryKey: paymentKeys.referralFriends(userId ?? ''),
    queryFn: () => fetchReferralFriends(userId!),
    enabled: !!userId,
    staleTime: 60_000,
  })
}

export function usePaymentStatus(
  paymentId: string | undefined,
  enabled = false,
): ReturnType<typeof useQuery<{ status: string }>> {
  return useQuery({
    queryKey: paymentKeys.paymentStatus(paymentId ?? ''),
    queryFn: () => fetchPaymentStatus(paymentId!),
    enabled: !!paymentId && enabled,
    refetchInterval: 3000, // Poll every 3 seconds
    staleTime: 0,
  })
}

// ── Mutations ───────────────────────────────────────────────

export function useCreateCard(): ReturnType<typeof useMutation<CardCreateResult, Error, { cardNumber: string; cardExpire: string }>> {
  return useMutation({
    mutationFn: ({ cardNumber, cardExpire }) => createCard(cardNumber, cardExpire),
  })
}

export function useVerifyCard(): ReturnType<typeof useMutation<{ success: boolean; is_default: boolean }, Error, { cardId: string; token: string; code: string }>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ cardId, token, code }) => verifyCard(cardId, token, code),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: paymentKeys.cards })
    },
  })
}

export function useRemoveCard(): ReturnType<typeof useMutation<{ success: boolean }, Error, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (cardId: string) => removeCard(cardId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: paymentKeys.cards })
    },
  })
}

export function useCalculatePayment(): ReturnType<typeof useMutation<PaymentCalculation, Error, { plan: string; period: string; promoCode?: string }>> {
  return useMutation({
    mutationFn: ({ plan, period, promoCode }) => calculatePayment(plan, period, promoCode),
  })
}

export function useValidatePromo(): ReturnType<typeof useMutation<PromoValidation, Error, string>> {
  return useMutation({
    mutationFn: (code: string) => validatePromo(code),
  })
}

export function useCreateCheckout(): ReturnType<typeof useMutation<CheckoutResult, Error, Parameters<typeof createCheckout>[0]>> {
  return useMutation({
    mutationFn: createCheckout,
  })
}

export function useSubscribeWithCard(): ReturnType<typeof useMutation<SubscribeResult, Error, Parameters<typeof subscribeWithCard>[0]>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: subscribeWithCard,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payment'] })
    },
  })
}

export function useCreateGift(): ReturnType<typeof useMutation<CreateGiftResult, Error, Parameters<typeof createGift>[0]>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createGift,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payment'] })
    },
  })
}

export function useRedeemGift(): ReturnType<typeof useMutation<RedeemGiftResult, Error, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => redeemGiftCode(code),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payment'] })
    },
  })
}

export function useRequestWithdrawal(): ReturnType<typeof useMutation<WithdrawalResult, Error, { cardNumber: string; cardHolder: string }>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ cardNumber, cardHolder }) => requestWithdrawal(cardNumber, cardHolder),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payment'] })
    },
  })
}
